import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { SignedInMethod, useAuth } from '@/features/auth';
import { isCameraCalibrated } from '@/utils/is-camera-calibrated';

import { useSettings } from '../hooks/use-settings';
import { CameraType } from '../types';
import {
  getBuddingMethods,
  getBuddingSensitivity,
  getCameraOptions,
  getCellSizeOptions,
  getChamberDepthOptions,
  getImagesPerAnalysisOptions,
  getLangugeOptions,
  getStainOptions,
  getSugarUnitOptions,
} from '../utils/dropdown-option-mapper';
import { SettingDropdownItem } from './settings-dropdown-item';
import { SettingsSwitch } from './settings-switch';
import { SettingsTextbox } from './settings-textbox';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  /** all the changes will be saved and open the camera calibration */
  onCameraCalibration: () => void;
}

export const UserSettingsModal: React.FC<IProps> = ({
  isOpen,
  toggle,
  onCameraCalibration,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { settings, setSettings } = useSettings();

  const { watch, control, handleSubmit } = useForm({
    defaultValues: settings,
  });

  // we show "calibrate camera" btn based on the value of camera dropdown
  const watchCameraType = watch('camera');
  const isMicroscopeCamera = useMemo(
    () => watchCameraType === CameraType.Lab,
    [watchCameraType],
  );

  // memorize options, since we don't wanna call helper functions on re-renders
  const langugeOptions = useMemo(() => getLangugeOptions(), []);
  const imagesPerAnalysisOptions = useMemo(() => getImagesPerAnalysisOptions(2, 30), []);
  const stainOptions = useMemo(() => getStainOptions(), []);
  const cellSizeOptions = useMemo(() => getCellSizeOptions(), []);
  const cameraOptions = useMemo(() => getCameraOptions(), []);
  const sugarUnitOptions = useMemo(() => getSugarUnitOptions(), []);
  const chamberOptions = useMemo(() => getChamberDepthOptions(), []);
  const buddingOptions = useMemo(() => getBuddingMethods(), []);
  const buddingSensitivityOptions = useMemo(() => getBuddingSensitivity(), []);

  const onSubmit = ({
    language,
    camera,
    detectedSquareSize,
    ...rest
  }: typeof settings & { language?: string }) => {
    // update language preference
    if (language !== i18n.language) {
      i18n.changeLanguage(language);
    }
    setSettings({ ...rest, camera }); // save data on local storage
    toast.success('User settings updated!'); // feedback notification
    toggle(); // close the modal

    // is user select the lab camera and save, go to calibration wizard
    if (
      camera === CameraType.Lab &&
      !isCameraCalibrated({ camera, detectedSquareSize: detectedSquareSize })
    )
      onCameraCalibration();
  };

  const handleOnCameraCalibration = () => {
    handleSubmit(onSubmit)();
    onCameraCalibration();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        title="header.settings"
        conatinerClassnames="w-full md:w-5/6 lg:w-2/3 xl:w-2/3 2xl:w-1/2"
      >
        <div className="flex flex-col gap-3 px-5 py-6">
          <SettingDropdownItem
            label="userSettings.language"
            control={control}
            name="language"
            options={langugeOptions}
            defaultValue={i18n.language}
          />

          <SettingDropdownItem
            label="userSettings.imagePerAnalysis"
            control={control}
            name="imagePerAnalysis"
            options={imagesPerAnalysisOptions}
            defaultValue={settings.imagePerAnalysis}
            parser={parseInt}
          />

          <SettingDropdownItem
            label="userSettings.stain"
            control={control}
            name="stain"
            options={stainOptions}
            defaultValue={settings.stain}
            parser={parseInt}
          />

          <SettingDropdownItem
            label="userSettings.cellSize"
            control={control}
            name="cellSize"
            options={cellSizeOptions}
            defaultValue={settings.cellSize}
            parser={parseInt}
          />

          <SettingDropdownItem
            control={control}
            name="chamberHeight"
            options={chamberOptions}
            defaultValue={settings.chamberHeight}
            label="userSettings.chamberHeight"
            parser={parseFloat}
          />

          {user?.signedInMethod === SignedInMethod.Beer ? (
            <SettingsSwitch
              control={control}
              name="elongatedCell"
              label="userSettings.elongatedCell"
              defaultValue={settings.elongatedCell}
            />
          ) : null}

          {user?.signedInMethod === SignedInMethod.Beer ||
          user?.signedInMethod === SignedInMethod.Wine ? (
            <SettingsSwitch
              control={control}
              name="lactobacillusDetection"
              label="userSettings.lactobacillusDetection"
              defaultValue={settings.lactobacillusDetection}
            />
          ) : null}

          {/* below feild will is only required for the wine */}
          {user?.signedInMethod === SignedInMethod.Wine ? (
            <SettingDropdownItem
              name="sugarUnit"
              parser={parseInt}
              control={control}
              options={sugarUnitOptions}
              defaultValue={settings.sugarUnit}
              label="userSettings.sugarUnit"
            />
          ) : null}

          {/* below feild will is only required for the ethanol */}
          {user?.signedInMethod === SignedInMethod.Ethanol ? (
            <SettingDropdownItem
              control={control}
              classnames="text-left text-sm"
              options={buddingOptions}
              name="ethanolBuddingMethod"
              defaultValue={settings.sugarUnit}
              label="userSettings.ethanolBuddingMethod"
              infoBubble={BuddingInfoBubble}
            />
          ) : null}

          {/* below feild will is only required for the ethanol */}
          {user?.signedInMethod === SignedInMethod.Ethanol ? (
            <SettingDropdownItem
              control={control}
              classnames="text-left text-sm"
              options={buddingSensitivityOptions}
              name="buddingSensitivity"
              defaultValue={settings.buddingSensitivity}
              label="userSettings.ethanolBuddingSensitivity"
            />
          ) : null}

          <div className="flex items-center justify-between">
            <p className="text-lg text-gray-600">Default values for sample dilution</p>
            <div className="flex flex-col gap-5 md:flex-row">
              <SettingsTextbox
                name="defaultDilutionSample1"
                label="measurements.sample"
                control={control}
                defaultValue={settings.defaultDilutionSample1}
              />
              <SettingsTextbox
                name="defaultDilutionSample2"
                label="measurements.water"
                control={control}
                defaultValue={settings.defaultDilutionSample2}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-lg text-gray-600">Default values for stain dilution</p>
            <div className="flex flex-col gap-5 md:flex-row">
              <SettingsTextbox
                name="defaultDilutionStain1"
                label="measurements.dilutionSample"
                control={control}
                defaultValue={settings.defaultDilutionStain1}
              />
              <SettingsTextbox
                name="defaultDilutionStain2"
                label="measurements.colorSolution"
                control={control}
                defaultValue={settings.defaultDilutionStain2}
              />
            </div>
          </div>

          <SettingDropdownItem
            label="userSettings.camera"
            control={control}
            name="camera"
            options={cameraOptions}
            defaultValue={settings.camera}
            parser={parseInt}
          />

          {/* we only show this button when user selects "Microscope camera" */}
          {isMicroscopeCamera ? (
            <div className="flex justify-end">
              <div>
                <Button
                  variant={ButtonVariant.Link}
                  classnames="underline"
                  onClick={handleOnCameraCalibration}
                >
                  {t('calibration.calibrateButton')}
                </Button>
              </div>
            </div>
          ) : null}
          {/* end: calibrate camera button */}
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSubmit(onSubmit)}>{t('userSettings.save')}</Button>
        </div>
      </Modal>
    </>
  );
};

const BuddingInfoBubble = (
  <div className="flex flex-col gap-3 p-4 text-sm">
    <p className="text-justify">
      If <span className="font-medium">based on all cells involved in budding</span> is
      selected all cells in a budding-pair are counted towards the budding index. The
      maximum attainable budding index is 100%. Example: If out of 10 cells, 8 are single
      cells and one is a mother cell and one is a connected bud (daughter cell) the
      budding index is 20%.
    </p>
    <p className="text-justify">
      If <span className="font-medium">based only on the buds</span> is selected only buds
      are counted towards the budding index. The maximum attainable budding index is 50%.
      Example: If out of 10 cells, 8 are single cells and one is a mother cell and one is
      a connected bud (daughter cell) the budding index is 10%.
    </p>
  </div>
);
